import type { SeedIsland } from './seedLatin'
import { MORE_ISLANDS_ROMANCE } from './islandsMoreRomance'
import { EXTRA_DE } from './islandsExtraDe'
import { EXTRA_ROMANCE } from './islandsExtraRomance'
import { NUMBERS_LATIN } from './islandsNumbersLatin'

/**
 * The expansion set: eight further topics per language, beyond the four
 * introductory ones.
 *
 * Chosen for coverage rather than variety — between them these are most of what
 * an adult actually does in a foreign country in a week: find somewhere, get
 * somewhere, buy something, arrange something, complain about something, and
 * hold an opinion. The introductory four get you through a first meeting; these
 * get you through the days after it.
 *
 * Every language carries the same eight, so switching sites feels like the same
 * app and a learner of two languages is drilling comparable material.
 */
const BASE_DE: SeedIsland[] = [
    {
      slug: 'wohnen',
      title: 'Where I live',
      nativeTitle: 'Wo ich wohne',
      useWhen: 'Pairs with the work island — somebody always asks where you came from.',
      tags: ['introductions', 'place'],
      level: 2,
      blocks: [
        ['Ich wohne in einer kleinen Stadt im Mittleren Westen.', 'I live in a small city in the Midwest.'],
        [
          'Hier ist nicht viel los, aber genau das gefällt mir.',
          "There isn't much going on here, but that's exactly what I like.",
          'Es ist viel los is the everyday way to say a place is busy.',
        ],
        ['Die Winter sind lang und ziemlich kalt.', 'The winters are long and rather cold.'],
        [
          'Bis in die Innenstadt fahre ich etwa eine Viertelstunde.',
          "It's about a quarter of an hour into town.",
          'eine Viertelstunde, not fünfzehn Minuten, in speech.',
        ],
        ['Ich wohne gern dort, aber ich vermisse das Meer.', 'I like living there, but I miss the sea.'],
      ],
    },
    {
      slug: 'freizeit',
      title: 'What I do outside work',
      nativeTitle: 'Meine Freizeit',
      useWhen: 'The island that keeps a conversation going once the basics are done.',
      tags: ['introductions', 'hobbies'],
      level: 2,
      blocks: [
        ['In meiner Freizeit lese ich viel und laufe gern.', 'In my free time I read a lot and enjoy running.'],
        [
          'Am Wochenende laufe ich meistens zehn Kilometer.',
          'At the weekend I usually run ten kilometres.',
          'laufen covers running and walking; joggen removes the doubt.',
        ],
        ['Außerdem interessiere ich mich sehr fürs Kochen.', "I'm also very interested in cooking.", 'sich interessieren für + accusative; fürs is für + das.'],
        [
          'In letzter Zeit lerne ich, deutsche Gerichte zu kochen.',
          "Lately I've been learning to cook German dishes.",
          'German has no continuous tense — the present does the work.',
        ],
        ['Besonders gut gelingt mir bisher nichts.', 'So far nothing turns out particularly well.'],
      ],
    },
    {
      slug: 'unterwegs',
      title: 'Getting around',
      nativeTitle: 'Unterwegs',
      useWhen: 'You need this while tired and carrying luggage. Overlearn it.',
      tags: ['transactional', 'travel'],
      level: 2,
      blocks: [
        ['Entschuldigung, wie komme ich zum Hauptbahnhof?', 'Excuse me, how do I get to the main station?', 'wie komme ich zu… is the phrase; zum = zu dem.'],
        ['Fährt dieser Bus zum Flughafen?', 'Does this bus go to the airport?'],
        ['Einmal nach München, bitte — hin und zurück.', 'One to Munich, please — return.', 'Einmal nach X is how you buy a ticket; nobody says eine Fahrkarte.'],
        ['Auf welchem Gleis fährt der Zug ab?', 'Which platform does the train leave from?'],
        ['Können Sie mir sagen, wann ich aussteigen muss?', 'Could you tell me when I have to get off?', 'The indirect question sends the verb to the end: wann ich aussteigen *muss*.'],
      ],
    },
    {
      slug: 'einkaufen',
      title: 'Shopping',
      nativeTitle: 'Einkaufen',
      useWhen: 'Short, frequent exchanges where hesitating costs you the queue.',
      tags: ['transactional', 'shopping'],
      level: 1,
      blocks: [
        ['Entschuldigung, haben Sie das auch in einer anderen Größe?', 'Excuse me, do you have this in another size?'],
        ['Ich schaue mich nur um, danke.', "I'm just looking, thanks.", 'The stock answer to Kann ich Ihnen helfen? Have it ready.'],
        ['Was kostet das?', 'How much is this?'],
        ['Kann ich mit Karte zahlen?', 'Can I pay by card?', 'Worth asking in Germany, where cash is still common.'],
        ['Ich nehme das, danke.', "I'll take it, thanks."],
      ],
    },
    {
      slug: 'smalltalk',
      title: 'Weather and small talk',
      nativeTitle: 'Smalltalk',
      useWhen: 'The filler that keeps a silence from becoming awkward.',
      tags: ['social'],
      level: 1,
      blocks: [
        ['Schönes Wetter heute, oder?', 'Lovely weather today, isn’t it?', '…oder? is the everyday tag question.'],
        ['Endlich wird es wärmer.', "It's finally getting warmer."],
        ['So ein Wetter hatten wir schon lange nicht mehr.', "We haven't had weather like this in ages."],
        ['Was für ein langer Tag.', 'What a long day.'],
        ['Das Wochenende kam dieses Mal wie gerufen.', 'The weekend came at just the right time this week.', 'wie gerufen kommen — to come just when needed.'],
      ],
    },
    {
      slug: 'verabreden',
      title: 'Making plans',
      nativeTitle: 'Sich verabreden',
      useWhen: 'The moment a conversation could become a second meeting.',
      tags: ['social'],
      level: 2,
      blocks: [
        ['Hättest du Lust, mal einen Kaffee trinken zu gehen?', 'Would you fancy going for a coffee sometime?', 'Hättest du Lust… is the standard, unpushy invitation.'],
        ['Wann würde es dir denn passen?', 'When would suit you?'],
        ['Bei mir geht es unter der Woche eigentlich immer.', 'During the week works for me pretty much always.'],
        ['Sollen wir sagen Donnerstag um sieben?', 'Shall we say Thursday at seven?'],
        ['Ich schreibe dir dann noch, wo genau.', "I'll message you where exactly.", 'schreiben covers texting; simsen is long dead.'],
      ],
    },
    {
      slug: 'probleme',
      title: 'When something goes wrong',
      nativeTitle: 'Wenn etwas schiefgeht',
      useWhen: 'The island you will need under stress, which is when improvising fails.',
      tags: ['transactional', 'problems'],
      level: 2,
      blocks: [
        ['Entschuldigung, ich glaube, hier stimmt etwas nicht.', 'Excuse me, I think something is wrong here.', 'Softer and more usable than a flat complaint.'],
        ['Ich habe das gestern bestellt, aber es ist nie angekommen.', 'I ordered this yesterday, but it never arrived.'],
        ['Könnten Sie mir bitte helfen?', 'Could you help me, please?'],
        ['Ich verstehe das leider nicht ganz — könnten Sie das noch einmal erklären?', "I'm afraid I don't quite understand — could you explain that again?"],
        ['Können Sie bitte etwas langsamer sprechen?', 'Could you speak a little more slowly, please?', 'Worth more than any other sentence in the language early on.'],
      ],
    },
    {
      slug: 'meinung',
      title: 'Opinions and preferences',
      nativeTitle: 'Meine Meinung',
      useWhen: 'What turns an interview into a conversation.',
      tags: ['social', 'opinions'],
      level: 3,
      blocks: [
        ['Ich finde, das ist eine gute Idee.', 'I think that’s a good idea.', 'finden, not denken, for opinions about things.'],
        ['Da bin ich mir nicht so sicher.', "I'm not so sure about that.", 'The polite way to disagree without saying no.'],
        ['Ehrlich gesagt gefällt mir das nicht besonders.', "Honestly, I don't much like it."],
        ['Das kommt darauf an.', 'That depends.'],
        ['Ich sehe das eigentlich genauso.', 'I see it much the same way, actually.'],
      ],
    },
  ]

/**
 * Every language's full island set.
 *
 * The expansion sets are merged per language rather than appended to one list,
 * so adding a topic to one language cannot silently land in another — the bug
 * class this app keeps producing.
 */
export const MORE_ISLANDS: Record<string, SeedIsland[]> = Object.fromEntries(
  ['fr', 'es', 'it'].map((code) => [
    code,
    [
      ...(MORE_ISLANDS_ROMANCE[code] ?? []),
      ...(EXTRA_ROMANCE[code] ?? []),
      ...(NUMBERS_LATIN[code] ?? []),
    ],
  ]),
)

MORE_ISLANDS.de = [...BASE_DE, ...EXTRA_DE, ...(NUMBERS_LATIN.de ?? [])]
